// @flow

import { all, put, takeEvery, call, select } from "redux-saga/effects";
import type { Action } from "redux-action";
import http from "@http";
import config from "@config";
import { dataURLtoFile } from "@utils/file";
import { isFirstCareUser } from "@modules/account/selectors";
import actionCreators from "@modules/service/actionCreator";
import { AddRtwAttachment, DeleteRtwAttachment } from "@modules/service/constants/actions";
import { sendSuccessMessage } from "@modules/app/actionCreator";

type RtwAddAttachmentRequest = $Exact<{
  clientId: number,
  processId: number,
  form: Object,
}>;

export function* onAddRtwAttachment({
  payload,
}: Action<RtwAddAttachmentRequest>): Generator<*, void, *> {
  yield put(actionCreators.ChangeAddFileInProgress({ addFileInProgress: true }));

  try {
    if (!payload || !payload.clientId || !payload.processId || !payload.form) {
      throw Error("Invalid payload provided");
    }

    const file = dataURLtoFile(payload.form.file);

    // Preparing "multipart/form-data" request
    const data = new FormData();
    data.append("file", file.blob, decodeURIComponent(file.name));

    yield call(
      http.post,
      `${config.endpointBaseUrl}api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/rtw-interview/process/${payload.processId}/draft/file`,
      data,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );

    const firstCareUser = yield select(isFirstCareUser);
    if (firstCareUser) {
      yield call(
        http.post,
        `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/rtw-interview/process/${payload.processId}/submit-for-review`,
        {
          notes: "RTW attachment added and submitted for review",
        },
      );
    }

    yield all([
      put(actionCreators.AddRtwAttachmentSuccess(data)),
      put(actionCreators.SetShowAddRtwAttachmentModal(false)),
      put(actionCreators.ChangeAddFileInProgress({ addFileInProgress: false })),
      put(actionCreators.GetRtw({ clientId: payload.clientId, processId: payload.processId })),
      put(sendSuccessMessage("RTW_ATTACHMENT_ADDED")),
    ]);
  } catch (e) {
    yield all([
      put(actionCreators.AddRtwAttachmentError(e.message)),
      put(actionCreators.ChangeAddFileInProgress({ addFileInProgress: false })),
    ]);
  }
}

type RtwDeleteAttachmentRequest = $Exact<{
  clientId: number,
  processId: number,
  attachmentId: number,
}>;

export function* onDeleteRtwAttachment({
  payload,
}: Action<RtwDeleteAttachmentRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.processId || !payload.attachmentId) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.delete,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/rtw-interview/process/${payload.processId}/draft/file/${payload.attachmentId}`,
    );

    const firstCareUser = yield select(isFirstCareUser);
    if (firstCareUser) {
      yield call(
        http.post,
        `${config.endpointBaseUrl}api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/rtw-interview/process/${payload.processId}/submit-for-review`,
        {
          notes: "RTW attachment removed and submitted for review",
        },
      );
    }

    yield all([
      put(actionCreators.DeleteRtwAttachmentSuccess(response.data)),
      put(actionCreators.GetRtw({ clientId: payload.clientId, processId: payload.processId })),
      put(sendSuccessMessage("RTW_ATTACHMENT_REMOVED")),
    ]);
  } catch (e) {
    yield put(actionCreators.DeleteRtwAttachmentError(e.message));
  }
}

// ROOT

export function* RtwAttachmentsSagas(): Generator<*, void, void> {
  yield all([
    takeEvery(AddRtwAttachment, onAddRtwAttachment),
    takeEvery(DeleteRtwAttachment, onDeleteRtwAttachment),
  ]);
}

export default RtwAttachmentsSagas;
